import { Col, FloatingLabel, Row, Card } from "react-bootstrap";
import { Form, Button } from "react-bootstrap";
import { useState, useEffect } from "react";
import PageLayout from "../../../layouts/PageLayout";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import moment from "moment";

// Edit all follow-ups of one family on one screen and save them together
export default function EditFamilyFollowUps() {
  const { person_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [person, setPerson] = useState(null);
  const [followupStatuses, setFollowupStatuses] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [errors, setErrors] = useState({});

  const AuthToken = localStorage.getItem("AuthUser");
  axios.defaults.headers.common["Authorization"] = `Bearer ${AuthToken}`;

  const imageUrl = (path) => (path ? process.env.REACT_APP_IMG_URL + path : null);

  // Rescue date is stored as YYYY-MM-DD; show it as e.g. 28 February 2026
  const formatDate = (date) => {
    const d = moment(date, "YYYY-MM-DD", true);
    return d.isValid() ? d.format("DD MMMM YYYY") : "N/A";
  };

  // Time since rescue, e.g. "1 year, 2 months, 5 days ago"
  const timeSince = (date) => {
    const from = moment(date, "YYYY-MM-DD", true);
    if (!from.isValid()) return "N/A";

    const today = moment().startOf("day");
    if (from.isAfter(today)) return "N/A";

    const years = today.diff(from, "years");
    from.add(years, "years");
    const months = today.diff(from, "months");
    from.add(months, "months");
    const days = today.diff(from, "days");

    const parts = [
      [years, "year"],
      [months, "month"],
      [days, "day"],
    ]
      .filter(([n]) => n > 0)
      .map(([n, label]) => `${n} ${label}${n === 1 ? "" : "s"}`);

    return parts.length ? `${parts.join(", ")} ago` : "Today";
  };

  // --------------------------------------------------
  // MAP API FOLLOW-UP TO FORM STATE (same rules as AddNewFollowUp)
  // --------------------------------------------------
  const toFormState = (d) => {
    let ans = {};
    let ansTxt = {};

    d.questions?.forEach((a) => {
      const value = (a.ans || "").toLowerCase();
      ans[a.followup_question_id] = value === "no" ? 0 : 1;
      if (value !== "no" && !a.question?.file) {
        ansTxt[a.followup_question_id] = a.ans;
      }
    });

    return {
      id: d.id,
      typeName: d.type?.name || "",
      userName: d.user?.name || "",
      dateOfVisit: d.date_of_visit || "",
      followUp: d.follow_up_id,
      comment: d.comment || "",
      needs: d.needs || "",
      img_1: null,
      img_2: null,
      img_3: null,
      preview: {
        img_1: imageUrl(d.family_home_image),
        img_2: imageUrl(d.family_business_image),
        img_3: imageUrl(d.children_school_image),
      },
      answers: ans,
      answersText: ansTxt,
    };
  };

  // ---------------------------------------------
  // LOAD FAMILY + ALL ITS FOLLOW-UPS
  // ---------------------------------------------
  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_URL}/family-followups/${person_id}`)
      .then((res) => {
        setPerson(res.data.person);
        setFollowupStatuses(res.data.followup_statuses);
        setQuestions(res.data.questions);
        setFollowups(res.data.followups.map(toFormState));
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        alert(err.response?.data?.message || "An error occurred");
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person_id]);

  // -----------------------------------------
  // UPDATE ONE FOLLOW-UP IN STATE
  // -----------------------------------------
  const updateFollowup = (index, changes) => {
    setFollowups((prev) =>
      prev.map((f, i) => (i === index ? { ...f, ...changes } : f))
    );
  };

  const handleChange = (index, e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      const file = files[0];
      const current = followups[index];
      updateFollowup(index, {
        [name]: file,
        preview: {
          ...current.preview,
          [name]: file ? URL.createObjectURL(file) : current.preview[name],
        },
      });
    } else {
      updateFollowup(index, { [name]: value });
    }
  };

  // -----------------------------------------
  // SUBMIT ALL FOLLOW-UPS IN ONE REQUEST
  // -----------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    followups.forEach((f, i) => {
      const key = `followups[${i}]`;
      formData.append(`${key}[id]`, f.id);
      formData.append(`${key}[follow_up_id]`, f.followUp);
      formData.append(`${key}[date_of_visit]`, f.dateOfVisit);
      formData.append(`${key}[comment]`, f.comment || "");
      formData.append(`${key}[needs]`, f.needs || "");

      if (f.img_1) formData.append(`${key}[family_home_image]`, f.img_1);
      if (f.img_2) formData.append(`${key}[family_business_image]`, f.img_2);
      if (f.img_3) formData.append(`${key}[children_school_image]`, f.img_3);

      Object.keys(f.answers).forEach((qid) => {
        formData.append(`${key}[questions][]`, qid);
        formData.append(`${key}[ans][${qid}]`, f.answers[qid]);

        if (f.answersText[qid]) {
          formData.append(`${key}[ans_text][${qid}]`, f.answersText[qid]);
        }
      });
    });

    setSaving(true);
    setErrors({});
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/update-family-followups/${person_id}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setFollowups(res.data.followups.map(toFormState));
      alert(res.data.message);
    } catch (err) {
      console.error(err);
      if (err.response?.data?.errors) {
        setErrors(err.response.data.errors);
        alert("Please fix the highlighted follow-ups.");
      } else {
        alert(err.response?.data?.message || "An error occurred");
      }
    } finally {
      setSaving(false);
    }
  };

  // Validation errors for one follow-up, e.g. "followups.1.date_of_visit"
  const errorsFor = (index) =>
    Object.keys(errors)
      .filter((k) => k.startsWith(`followups.${index}.`))
      .map((k) => errors[k][0]);

  const familyPics = [
    { field: "img_1", text: "Home Image", icon: "house" },
    { field: "img_2", text: "Family Business Image", icon: "apartment" },
    { field: "img_3", text: "Children School Image", icon: "school" },
  ];

  if (loading) return <div>Loading...</div>;

  return (
    <PageLayout>
      <Row>
        <Col xl={12}>
          <div className="mc-card">
            <div className="mc-breadcrumb">
              <h3 className="mc-breadcrumb-title">
                {"Edit Family Follow Ups"}
              </h3>
              <ul className="mc-breadcrumb-list">
                <li className="mc-breadcrumb-item">
                  <Link to="/family-follow-ups" className="mc-breadcrumb-link" style={{ color: "#077834" }}>
                    {"FollowUps"}
                  </Link>
                </li>
                <li className="mc-breadcrumb-item">{person?.beneficiary_name}</li>
              </ul>
            </div>
          </div>
        </Col>
      </Row>

      {/* FAMILY DETAILS */}
      <Row className="mb-3">
        <Form.Group as={Col} md="4" className="mb-3">
          <FloatingLabel label="Person's Name">
            <Form.Control value={person?.beneficiary_name || ""} readOnly />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md="4" className="mb-3">
          <FloatingLabel label="Family Code">
            <Form.Control value={person?.f_number || ""} readOnly />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md="4" className="mb-3">
          <FloatingLabel label="Rescue Date">
            <Form.Control value={formatDate(person?.rescue_date)} readOnly />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md="4">
          <FloatingLabel label="CNIC">
            <Form.Control value={person?.cnic || ""} readOnly />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md="4">
          <FloatingLabel label="Mobile Number">
            <Form.Control value={person?.mob || ""} readOnly />
          </FloatingLabel>
        </Form.Group>
        <Form.Group as={Col} md="4">
          <FloatingLabel label="Time Since Rescue">
            <Form.Control value={timeSince(person?.rescue_date)} readOnly />
          </FloatingLabel>
        </Form.Group>
      </Row>

      {followups.length === 0 && <p>No follow-ups found for this family.</p>}

      <Form onSubmit={handleSubmit}>
        {followups.map((f, index) => (
          <Card key={f.id} className="mb-4">
            <Card.Body>
              <Card.Title>
                {`Follow Up ${index + 1} of ${followups.length}: ${f.typeName}`}
                {f.userName && (
                  <small className="text-muted ms-2">{`(Entered by ${f.userName})`}</small>
                )}
                <Link to={`/view-follow-up/${f.id}`} className="ms-2" style={{ fontSize: "14px" }}>
                  View
                </Link>
              </Card.Title>

              {errorsFor(index).length > 0 && (
                <div className="alert alert-danger py-2">
                  {errorsFor(index).map((msg, i) => (
                    <div key={i}>{msg}</div>
                  ))}
                </div>
              )}

              <Row className="mb-3">
                {/* Date */}
                <Form.Group as={Col} md="4">
                  <FloatingLabel label="Date Of Visit">
                    <Form.Control
                      required
                      type="date"
                      name="dateOfVisit"
                      value={f.dateOfVisit}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </FloatingLabel>
                </Form.Group>

                {/* Follow Up */}
                <Form.Group as={Col} md="4">
                  <FloatingLabel label="Follow Up Status">
                    <Form.Select
                      required
                      name="followUp"
                      value={f.followUp}
                      onChange={(e) => handleChange(index, e)}
                    >
                      <option value="">Select...</option>
                      {followupStatuses.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Form.Select>
                  </FloatingLabel>
                </Form.Group>
              </Row>

              {/* Comments */}
              <Row>
                <Form.Group as={Col} md="6">
                  <FloatingLabel label="Comment">
                    <Form.Control
                      as="textarea"
                      name="comment"
                      value={f.comment}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </FloatingLabel>
                </Form.Group>

                <Form.Group as={Col} md="6">
                  <FloatingLabel label="Needs">
                    <Form.Control
                      as="textarea"
                      name="needs"
                      value={f.needs}
                      onChange={(e) => handleChange(index, e)}
                    />
                  </FloatingLabel>
                </Form.Group>
              </Row>

              {/* FAMILY IMAGES */}
              <Row className="mt-4">
                {familyPics.map((field) => (
                  <Col key={field.field} md={4}>
                    <Card>
                      <Card.Body>
                        <Card.Title>{field.text}</Card.Title>

                        <label
                          htmlFor={`file-input-${f.id}-${field.field}`}
                          style={{
                            width: "100%",
                            height: "150px",
                            border: "1px dashed #ccc",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            cursor: "pointer",
                          }}
                        >
                          {f.preview[field.field] ? (
                            <img
                              src={f.preview[field.field]}
                              alt={field.text}
                              style={{
                                width: "100%",
                                height: "145px",
                                objectFit: "contain",
                              }}
                            />
                          ) : (
                            <i className="material-icons">{field.icon}</i>
                          )}
                        </label>

                        <input
                          id={`file-input-${f.id}-${field.field}`}
                          type="file"
                          accept="image/png,image/jpeg"
                          style={{ display: "none" }}
                          name={field.field}
                          onChange={(e) => handleChange(index, e)}
                        />
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>

              {/* QUESTIONS */}
              <Row className="mt-4">
                <Col md={12}>
                  <h5>Follow-up Questions</h5>

                  {questions.map((q) => (
                    <div key={q.id} className="mb-3 pb-2 border-bottom">
                      <b>{q.question}</b>

                      <div className="mt-2">
                        <Form.Check
                          required
                          inline
                          label="Yes"
                          type="radio"
                          name={`answer-${f.id}-${q.id}`}
                          checked={f.answers[q.id] === 1}
                          onChange={() =>
                            updateFollowup(index, { answers: { ...f.answers, [q.id]: 1 } })
                          }
                        />

                        <Form.Check
                          required
                          inline
                          label="No"
                          type="radio"
                          name={`answer-${f.id}-${q.id}`}
                          checked={f.answers[q.id] === 0}
                          onChange={() =>
                            updateFollowup(index, { answers: { ...f.answers, [q.id]: 0 } })
                          }
                        />
                      </div>

                      {f.answers[q.id] === 1 && (
                        <Form.Control
                          className="mt-2"
                          placeholder="Write details..."
                          value={f.answersText[q.id] || ""}
                          onChange={(e) =>
                            updateFollowup(index, {
                              answersText: { ...f.answersText, [q.id]: e.target.value },
                            })
                          }
                        />
                      )}
                    </div>
                  ))}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        ))}

        {followups.length > 0 && (
          <Button type="submit" className="mt-3" variant="success" disabled={saving}>
            {saving ? "Saving..." : `Update All ${followups.length} Follow Ups`}
          </Button>
        )}
      </Form>
    </PageLayout>
  );
}
